/* eslint-disable  @typescript-eslint/no-explicit-any */
import type { ReduxAction, ReduxActionMethod, ReduxReducer, ReduxSelector, ReduxState } from '../types';
import { createActionBuilder } from './createActionBuilder.ts';
import { createSelector } from './createSelector.ts';
import { capitalize } from '../../utils/string/capitalize.ts';

export interface ReduxReducerBuilder<
  ActionTypes extends string,
  ReducerName extends string,
  State extends object, // this is the state after splitting up
  TopState extends object = ReduxState, // this is the top-level state object, type of getStore().getState()
  Actions extends Record<string, ReduxActionMethod<any>> = Record<string, ReduxActionMethod<any>>,
  Selectors extends { [methodName: string]: ReduxSelector<unknown, TopState> } = Record<string, ReduxSelector<unknown, TopState>>
> {
  add<DataType, ActionType extends ActionTypes, Key extends string>(actionType: ActionType, key: Key, defaultValue: DataType):
    ReduxReducerBuilder<
      ActionTypes,
      ReducerName,
      State,
      TopState,
      Actions & { [key in `update${Capitalize<Key>}`]: ReduxActionMethod<[value: DataType]> },
      Selectors & { [key in `${Key}Selector`]: ReduxSelector<DataType, TopState> }
    >;

  addAction<Name extends string, ActionMethod extends ReduxActionMethod<any>>(name: Name, action: ActionMethod):
    ReduxReducerBuilder<
      ActionTypes,
      ReducerName,
      State,
      TopState,
      Actions & { [key in Name]: ActionMethod },
      Selectors
    >;

  addSelector<Name extends string, Selector extends ReduxSelector<unknown, TopState>>(name: Name, selector: Selector):
    ReduxReducerBuilder<
      ActionTypes,
      ReducerName,
      State,
      TopState,
      Actions,
      Selectors & { [key in Name]: Selector }
    >;

  addReducer<ActionType extends ActionTypes, Properties extends object = object>(actionType: ActionType, method: ReduxReducer<State, ActionType, Properties>):
    ReduxReducerBuilder<ActionTypes, ReducerName, State, TopState, Actions, Selectors>;

  build(): { [r in ReducerName]: (state: State | undefined, action: ReduxAction<ActionTypes, any>) => Partial<State> } & Actions & Selectors;
}

/**
 * @example
 * type Actions = 'Letter';
 * const { abc, updateLetter, letterSelector } = createReducerBuilder<Actions, 'abc'>('abc')
 *   .add('Letter', 'abc', undefined),
 */
export function createReducerBuilder<
  ActionTypes extends string,
  ReducerName extends string,
  State extends object = Record<string, unknown>,
  TopState extends object = ReduxState,
  Actions extends Record<string, ReduxActionMethod<any>> = Record<string, ReduxActionMethod<any>>,
  Selectors extends { [methodName: string]: ReduxSelector<unknown, TopState> } = Record<string, ReduxSelector<unknown, TopState>>
>(reducerName: ReducerName): ReduxReducerBuilder<ActionTypes, ReducerName, State, TopState, Actions, Selectors> {
  const actions: Record<string, ReduxActionMethod<any>> = {};
  const selectors: { [methodName: string]: ReduxSelector<unknown, TopState & { [r in ReducerName]: State }> } = {};
  const reducers: Partial<Record<ActionTypes, ReduxReducer<State, any, any>>> = {};

  return {
    /**
     * Adds a new action, selector, and reducer.
     * The action is named `update${key}` (first letter of key capitalized) and the selector is `${key}Selector`.
     * @example
     * add('cake', 'chocolate'); // implicit add<'cake', string>('cake', 'chocolate')
     * // equivalent to
     * const updateCake(cake: string) {
     *   dispatch({ ...state, }
     * }
     * const cakeSelector(state: ReduxState): string
     */
    add<ActionType extends ActionTypes, Key extends string, DataType>(actionType: ActionType, key: Key, defaultValue: DataType) {
      actions[`update${capitalize(key)}`] = createActionBuilder(actionType, (value: DataType) => ({ [key]: value }));

      selectors[`${key}Selector`] = createSelector<DataType, DataType, TopState>(`${reducerName}.${key}`, defaultValue);

      reducers[actionType] = ((state: Partial<State>, action: { type: ActionType, [key]: DataType }) =>
        ({ ...state, [key]: action[key] })) as ReduxReducer<State, ActionType>;

      return this;
    },

    /**
     * Directly adds an arbitrary action
     */
    addAction<Name extends string, ActionMethod extends ReduxActionMethod>(name: Name, action: ActionMethod) {
      actions[name] = action;

      return this as ReduxReducerBuilder<ActionTypes, ReducerName, State, TopState, Actions & { [key in Name]: ActionMethod }, Selectors>
    },

    /**
     * Directly adds an arbitrary selector
     */
    addSelector<Name extends string, Selector extends ReduxSelector<unknown, TopState>>(name: Name, selector: Selector) {
      selectors[name] = selector;

      return this as ReduxReducerBuilder<ActionTypes, ReducerName, State, TopState, Actions, Selectors & { [key in Name]: Selector }>;
    },

    addReducer<ActionType extends ActionTypes, Properties extends object = object>(actionType: ActionType,
      method: ReduxReducer<State, ActionType, Properties>) {
      reducers[actionType] = method;

      return this;
    },

    build() {
      return {
        [reducerName]: (state: Partial<State> | undefined, action: ReduxAction<ActionTypes>): Partial<State> => {
          return (reducers[action.type] ?? ((state: Partial<State>) => state))(state ?? {}, action);
        },
        ...actions,
        ...selectors
      } as { [r in ReducerName]: (state: Partial<State> | undefined, action: ReduxAction) => Partial<State> } & Actions & Selectors;
    }
  };
}